import React, { useState, useEffect, useContext, createContext, ReactNode } from 'react';
import { ThemeConfig } from '@/types';

interface ThemeContextType {
  themeMode: 'light' | 'dark' | 'auto';
  toggleTheme: () => void;
  setThemeMode: (mode: 'light' | 'dark' | 'auto') => void;
  themeConfig: ThemeConfig;
  updateThemeConfig: (config: Partial<ThemeConfig>) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
}

const defaultThemeConfig: ThemeConfig = {
  mode: 'light',
  primaryColor: '#1976d2',
  secondaryColor: '#dc004e',
  fontSize: 'medium',
};

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [themeConfig, setThemeConfig] = useState<ThemeConfig>(() => {
    // Load theme from localStorage
    const savedTheme = localStorage.getItem('theme-config');
    if (savedTheme) {
      try {
        return { ...defaultThemeConfig, ...JSON.parse(savedTheme) };
      } catch (error) {
        console.error('Failed to parse saved theme config:', error);
      }
    }
    return defaultThemeConfig;
  });

  const toggleTheme = () => {
    const newMode = themeConfig.mode === 'light' ? 'dark' : 'light';
    updateThemeConfig({ mode: newMode });
  };

  const setThemeMode = (mode: 'light' | 'dark' | 'auto') => {
    updateThemeConfig({ mode });
  };

  const updateThemeConfig = (config: Partial<ThemeConfig>) => {
    const newConfig = { ...themeConfig, ...config };
    setThemeConfig(newConfig);
    localStorage.setItem('theme-config', JSON.stringify(newConfig));
  };

  // Apply theme to document root
  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute('data-theme', themeConfig.mode);
    
    // Set CSS custom properties
    root.style.setProperty('--primary-color', themeConfig.primaryColor);
    root.style.setProperty('--secondary-color', themeConfig.secondaryColor);
    
    // Set font size class
    root.className = root.className.replace(/font-size-\w+/g, '');
    root.classList.add(`font-size-${themeConfig.fontSize}`);
  }, [themeConfig]);

  const contextValue: ThemeContextType = {
    themeMode: themeConfig.mode,
    toggleTheme,
    setThemeMode,
    themeConfig,
    updateThemeConfig,
  };

  return React.createElement(
    ThemeContext.Provider,
    { value: contextValue },
    children
  );
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export default useTheme;
